import './style.css';

import type { Item } from 'bz-core-controls/controls/form/field/field-select/iprops';

import { useAction } from 'bz-utilities/hooks/action';
import useComputed from 'bz-utilities/hooks/computed';
import type { ConfigureCustomAttributeDto } from 'caseflow/api.dtos';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import toast from 'react-hot-toast';
import ResponsivePanel from '../layout/responsive-panel';
import type { ListEditorItem } from '../list-editor';
import ListEditor from '../list-editor';
import ListHeader from './controls/list-header';

export enum EChangeType {
  add = 'add',
  remove = 'remove',
  reorder = 'reorder'
}
export interface IChange {
  type: EChangeType;
  category: string;
  item: string;
}

export interface IProps {
  className?: string;
  canAdd?: boolean;
  canOrder?: boolean;
  canRemove?: boolean;
  currentCategory?: string;
  displayMember?: string;
  header?: React.ReactNode;
  categories: Item[];
  itemDisplayMember?: string;
  maxLength?: number;
  preventDuplicates?: boolean;
  selectorLabel?: string;
  selectValues?: boolean;
  valueMember?: string;
  valuesToAdd?: [];

  onCategoryChange?: (value: string) => void;
  onChange?: (type: EChangeType, value: string, category: string) => void;
  onReorder?: (items: ListEditorItem[]) => void;
}

export interface IState {
  category?: string;
}

const CustomList: React.FC<IProps> = observer(
  ({
    canAdd = true,
    className = '',
    currentCategory,
    displayMember = 'text',
    categories = [],
    selectorLabel = 'Please select a lookup',
    selectValues = false,
    valueMember = 'payload',
    canRemove,
    canOrder,
    header,
    maxLength,
    valuesToAdd,
    onChange,
    onReorder
  }) => {
    const [state] = React.useState<IState>(() => {
      return observable({
        category: currentCategory
      });
    });

    const currentList: ListEditorItem[] = useComputed(() => {
      const { category } = state;
      if (!category) return [];

      return (categories.find((c) => c[valueMember] === category)?.values ?? []).map(
        (x: string | ConfigureCustomAttributeDto, i: number) => {
          if (typeof x === 'string') return { id: x, text: x, order: i } as ListEditorItem;
          return { id: x.id, text: x.name, order: i, inUse: x.inUse } as ListEditorItem;
        }
      );
    }, [state, categories, valueMember]);

    const onAdd = useAction(
      async (value: string) => {
        const { category } = state;
        if (!category || currentList.some((l) => l.text === value)) return;
        onChange?.(EChangeType.add, value, category);
      },
      [state, currentList, onChange]
    );

    const onRemove = useAction(
      async (item: ListEditorItem) => {
        const { category } = state;
        if (!category) return;
        //quick solution, probably could look at allowing the delete button to be hidden
        if (category == 'VREMINDERS_BTYPES') {
          if (['MOT', 'Service'].indexOf(item.text) > -1) {
            toast.error('This item is reserved and can not be deleted.', {
              position: 'top-center'
            });
            return;
          }
        }
        onChange?.(EChangeType.remove, item.text, category);
      },
      [state, onChange]
    );

    return (
      <div className={`flex flex-col flex-nowrap ${className}`}>
        {header ?? (
          <ListHeader
            onCategoryChange={(c) => (state.category = c)}
            category={state.category}
            selectorLabel={selectorLabel}
            items={categories}
            displayMember={displayMember}
            valueMember={valueMember}
          />
        )}
        {!!state.category && (
          <ListEditor
            items={currentList}
            onReorder={onReorder}
            onRemove={onRemove}
            canOrder={canOrder}
            canRemove={canRemove}
            canAdd={canAdd}
            onAdd={onAdd}
            maxLength={maxLength}
            valuesToAdd={valuesToAdd}
            selectValues={selectValues}
            category={state.category}
          />
        )}
      </div>
    );
  }
);

export default CustomList;
